import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { axiosCatalogInstance } from "./base"
import type { ICatalogUser } from "@/shared/typings/catalogUser"
import { initData } from "@telegram-apps/sdk-react"
import { TokenPrice } from "../typings/tokenPrice"
import { Invoice } from "../typings/stars"
import { Token } from "../typings/token"
import { Task } from "../typings/task"
import { TokenBalance } from "../typings/tokenBalance"
import { useTranslation } from "react-i18next"
import { useContext } from "react"
import { ToastContext } from "@/app/providers/ToastProvider"

export const useGetCatalogUser = () => {
  const userData = initData.user()

  const { data: user, isLoading } = useQuery({
    queryKey: ["catalogUser"],
    queryFn: async (): Promise<ICatalogUser> => {
      return await axiosCatalogInstance.get(
        `${import.meta.env.VITE_CORE_API_URL}/users`
      )
    },
  })

  if (user && userData?.id)
    localStorage.setItem(`${userData.id}_catalogUser`, JSON.stringify(user))

  return { user, isLoading }
}

export const useGetCatalogBalances = () => {
  const { data: balances, isLoading } = useQuery({
    queryKey: ["balances"],
    queryFn: async (): Promise<TokenBalance[]> => {
      return axiosCatalogInstance.get(
        `${import.meta.env.VITE_CORE_API_URL}/users/balances`
      )
    },
  })

  return { balances, isLoading }
}

export const useGetAllTokens = () => {
  const { data: tokens, isLoading } = useQuery({
    queryKey: ["allTokens"],
    queryFn: async (): Promise<Token[]> => {
      return axiosCatalogInstance.get(
        `${import.meta.env.VITE_CORE_API_URL}/tokens/all`
      )
    },
  })

  return { tokens, isLoading }
}

export const useGetTokenPrices = () => {
  const { data: prices } = useQuery({
    queryKey: ["tokenPrices"],
    queryFn: async (): Promise<TokenPrice[]> => {
      return axiosCatalogInstance.get(
        `${import.meta.env.VITE_CORE_API_URL}/tokens/prices`
      )
    },
  })

  return { prices }
}

export const useGetTasks = () => {
  const { data: tasks } = useQuery({
    queryKey: ["tasks"],
    queryFn: async (): Promise<Task[]> => {
      return axiosCatalogInstance.get(
        `${import.meta.env.VITE_CORE_API_URL}/tasks/all`
      )
    },
  })

  return { tasks }
}

export const useGetUserTasks = () => {
  const { data: tasks } = useQuery({
    queryKey: ["userTasks"],
    queryFn: async (): Promise<Task[]> => {
      return axiosCatalogInstance.get(
        `${import.meta.env.VITE_CORE_API_URL}/tasks/all-by-user`
      )
    },
  })

  return { tasks }
}

export const useCompleteTask = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationKey: ["userMutation"],
    mutationFn: async (id: number) => {
      const data = await axiosCatalogInstance.post(
        `${import.meta.env.VITE_CORE_API_URL}/tasks/task-status/${id}`
      )

      return data
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["userTasks"],
      })
    },
  })

  return mutation
}

export const useTopupStars = () => {
  const queryClient = useQueryClient()
  const showToast = useContext(ToastContext)
  const { t } = useTranslation()

  return useMutation({
    mutationKey: ["topupStars"],
    mutationFn: async (params: { amount: number; locale: string }) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      return (await axiosCatalogInstance.post("/bot/topup-stars", {
        amount: params.amount,
        title: `${params.locale} ${params.amount} Stars`,
        description: "",
      })) as {
        invoice: Invoice
        link: string
      }
    },
    onError: () => {
      showToast?.(t("error.topup"), 5000)
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["userinfo"],
      })
      await queryClient.invalidateQueries({
        queryKey: ["balances"],
      })
    },
  })
}

export const useGetInvoiceStars = (params: {
  id: number
  enabled?: boolean
}) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["invoice", params.id],
    queryFn: async (): Promise<Invoice> => {
      return axiosCatalogInstance.get(
        `${import.meta.env.VITE_CORE_API_URL}/bot/invoice/${params.id}`
      )
    },
    enabled: params.enabled,
    refetchInterval: 500,
  })

  return { data, isLoading, error }
}
